import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { lgPopover, lgPress, lgControlBg, lgCardSheen, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { transition, fluid } from '../styles/motion';
import { usePresence } from '../hooks/usePresence';
import { usePressAction } from '../hooks/usePressAction';
import { fluidPop, fluidCascade } from './Fluid';
import { pageRect, pageViewport } from '../utils/appZoom';

/*
 * [MYSALES-REPORT 2026-10-28, user] "Download" — a button that opens a small menu right under it, with the app's dropdown
 * motion (it grows out of the button and its rows ripple in, components/Fluid), listing the file types to download.
 * Picking one makes and saves the file (the page's onPick) and closes the menu; while it is being made, that row shows a
 * spinner, and a problem is said inside the menu — a file is never made from a guess. Escape or a press outside closes
 * it (not while a file is being made, so its outcome is always seen). Portalled to <body> and placed in page px
 * (utils/appZoom), like EditColumnsMenu, so no parent can clip it.
 * Phones (compact): a round icon-only button, the switch's height — the word does not fit beside the switch at 360 px.
 */

export interface DownloadChoice {
  key: string;
  label: string;   // "Excel file"
  hint: string;    // one line on what is in it
  tag: string;     // ".xlsx"
  icon: string;    // Font Awesome classes, e.g. "fa-solid fa-file-excel"
  tone: 'green' | 'red';
}

interface Props {
  choices: DownloadChoice[];
  /** The menu's small heading, e.g. "Download · Apr – Sep 2026". */
  title: string;
  /** Called as the menu opens: the page starts getting the data ready, so a pick is quick. */
  onOpen?: () => void;
  /** Make and save the file. Resolves to a note to show instead of closing (e.g. nothing to download); rejects with
   *  an Error whose message is shown. */
  onPick: (key: string) => Promise<string | void>;
  /** Phones: the round icon-only button. */
  compact?: boolean;
}

const POP_WIDTH = 272;
const GAP = 6;
const MARGIN = 8;

/* The switch pill's look and height (styles/buttons pillTrack around 30px segments, plus the 1px border = 38px). */
const Trigger = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $open: boolean; $compact: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 38px;
  box-sizing: border-box;
  ${(p) => (p.$compact ? 'width: 38px; padding: 0;' : 'padding: 0 13px 0 15px;')}
  flex: none;
  border-radius: 999px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.2px;
  white-space: nowrap;
  cursor: pointer;
  color: ${(p) => p.theme.text};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.05)')};
  border: 1px solid ${(p) => (p.$open ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600) : p.theme.line)};
  box-shadow: 0 0 0 3px ${(p) => (p.$open ? (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.20)' : 'rgba(26,127,100,0.16)') : 'transparent')};
  transition: ${transition.hover}, ${transition.press};

  &:hover {
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.08)')};
    border-color: ${(p) => (p.$open ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600) : (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.45)' : 'rgba(26,127,100,0.40)'))};
  }
  &:active { transform: translateY(1px); }
  &:focus-visible {
    outline: 2px solid ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
    outline-offset: 2px;
  }

  i { font-size: 12px; }
  /* [LIQUID-GLASS 2026-11-02, user] */
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background-color: ${lgControlBg};
  background-image: ${lgCardSheen};
  ${lgPress}
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  width: 12px;
  height: 12px;
  color: ${(p) => p.theme.dim};
  transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
  transition: ${transition.hover}, transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const Popover = styled.div<{ $top: number; $left: number; $open: boolean; $up: boolean }>`
  position: fixed;
  top: ${(p) => p.$top}px;
  left: ${(p) => p.$left}px;
  width: ${POP_WIDTH}px;
  max-width: calc(100vw / var(--app-zoom, 1) - ${MARGIN * 2}px); /* [APP-FIT 2026-10-26] the window in page px */
  z-index: 1300;
  padding: 6px;
  box-sizing: border-box;
  border-radius: 12px;
  color: ${(p) => p.theme.text};
  background:
    linear-gradient(180deg, rgba(255, 255, 255, ${(p) => (p.theme.colorScheme === 'dark' ? 0.06 : 0.40)}) 0%, rgba(255, 255, 255, 0) 40%),
    ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(38, 38, 38, 0.985)' : 'rgba(250, 251, 251, 0.985)')};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(22, 25, 23, 0.10)')};
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, ${(p) => (p.theme.colorScheme === 'dark' ? 0.12 : 0.6)}),
    0 16px 40px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.55)' : 'rgba(22, 25, 23, 0.18)')};
  /* Grows out of the button on the spring (upward from its bottom corner when it opens upward); rows ripple in. */
  ${(p) => fluidPop({ open: p.$open, origin: `0 ${p.$up ? '100%' : '0'}`, from: `translateY(${p.$up ? 8 : -8}px) scale(0.92)` })}
  ${(p) => (p.$open ? fluidCascade('[data-dl-row]') : '')}
  /* [LIQUID-GLASS 2026-11-02, user] [STALE-PRESS 2026-11-02, user] glass without the live blur: it opens next to buttons
     (styles/liquidGlass.ts lgPopover says why) */
  ${lgPopover}
  border-color: transparent;
`;

const Heading = styled.div`
  padding: 6px 8px 7px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${(p) => p.theme.dim};

  @media (max-width: 768px) { font-size: 11.5px; }
`;

const Choice = styled.button<{ $busy: boolean }>`
  width: 100%;
  min-height: 50px;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 8px 10px;
  border: none;
  border-radius: 9px;
  background: ${(p) => (p.$busy ? (p.theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.06)' : p.theme.buttonHover) : 'transparent')};
  color: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: ${transition.hover};

  &:hover:not(:disabled) { background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.06)' : p.theme.buttonHover)}; }
  &:disabled { cursor: default; opacity: ${(p) => (p.$busy ? 1 : 0.5)}; }
  &:focus-visible {
    outline: 2px solid ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
    outline-offset: -2px;
  }
`;

/* The file type's tile: Excel green, PDF red, softly tinted for both themes. */
const IconTile = styled.span<{ $tone: 'green' | 'red' }>`
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  color: ${(p) => (p.$tone === 'green'
    ? (p.theme.colorScheme === 'dark' ? '#4ade80' : '#15803d')
    : (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626'))};
  background: ${(p) => (p.$tone === 'green'
    ? (p.theme.colorScheme === 'dark' ? 'rgba(74, 222, 128, 0.12)' : 'rgba(21, 128, 61, 0.09)')
    : (p.theme.colorScheme === 'dark' ? 'rgba(248, 113, 113, 0.12)' : 'rgba(220, 38, 38, 0.08)'))};
`;

const ChoiceText = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ChoiceLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
`;

const ChoiceHint = styled.span`
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.3;
  color: ${(p) => p.theme.dim};

  @media (max-width: 768px) { font-size: 12px; }
`;

const Tag = styled.span`
  flex: none;
  font-size: 11px;
  font-weight: 700;
  color: ${(p) => p.theme.dim};
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  flex: none;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(22, 25, 23, 0.14)')};
  border-top-color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  animation: ${spin} 0.8s linear infinite;
`;

const Note = styled.div<{ $tone: 'error' | 'info' }>`
  margin: 4px 4px 2px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  color: ${(p) => (p.$tone === 'error'
    ? (p.theme.colorScheme === 'dark' ? '#f87171' : '#b91c1c')
    : p.theme.dim)};
  background: ${(p) => (p.$tone === 'error'
    ? (p.theme.colorScheme === 'dark' ? 'rgba(248, 113, 113, 0.10)' : 'rgba(220, 38, 38, 0.06)')
    : (p.theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.035)'))};
`;

interface Placement { top: number; left: number; up: boolean }

const DownloadMenu: React.FC<Props> = ({ choices, title, onOpen, onPick, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ tone: 'error' | 'info'; text: string } | null>(null);
  const [pos, setPos] = useState<Placement>({ top: 0, left: 0, up: false });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const busyRef = useRef<string | null>(null);
  // Keeps the menu mounted through its close so the exit can play; `open` stays the source of truth for behaviour.
  const pres = usePresence(open, fluid.outMs);

  // Under the button when there is room; above it when there is clearly more room there. Left edges line up, and the
  // menu always stays on the screen.
  const place = useCallback(() => {
    const r = triggerRef.current ? pageRect(triggerRef.current) : null;
    if (!r) return;
    const { width: vw, height: vh } = pageViewport();
    const width = Math.min(POP_WIDTH, vw - MARGIN * 2);
    const height = popRef.current ? pageRect(popRef.current).height : 190;
    const below = vh - r.bottom - GAP - MARGIN;
    const above = r.top - GAP - MARGIN;
    const up = below < height && above > below;
    const top = up ? Math.max(MARGIN, r.top - GAP - height) : r.bottom + GAP;
    const left = Math.max(MARGIN, Math.min(r.left, vw - width - MARGIN));
    setPos((p) => (p.top === top && p.left === left && p.up === up ? p : { top, left, up }));
  }, []);

  const close = useCallback((refocus = false) => {
    if (busyRef.current) return;   // a file is being made: stay open until it is saved (or says why not)
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  const openMenu = () => {
    setNote(null);
    place();
    setOpen(true);
    onOpen?.();
  };
  const press = usePressAction<HTMLButtonElement>(() => (open ? close(false) : openMenu()));

  // With the real height known, place it once more (an upward menu needs it).
  useLayoutEffect(() => { if (open && pres.mounted) place(); }, [open, pres.mounted, note, place]);

  // Keyboard users land on the first choice (one frame later, after the press that opened it). Not on touch screens.
  useLayoutEffect(() => {
    if (!open || !pres.mounted) return undefined;
    const touch = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    if (touch) return undefined;
    const id = requestAnimationFrame(() => popRef.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(id);
  }, [open, pres.mounted]);

  // Follow the button while open; close on Escape or a press outside.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      close(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(true); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('touchstart', onDown, { passive: true });
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('touchstart', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, close, place]);

  const pick = async (key: string) => {
    if (busyRef.current) return;
    busyRef.current = key;
    setBusy(key);
    setNote(null);
    let message: string | void;
    try {
      message = await onPick(key);
    } catch (err: any) {
      setNote({ tone: 'error', text: err?.message || 'Could not make the file. Please try again.' });
      busyRef.current = null;
      setBusy(null);
      return;
    }
    busyRef.current = null;
    setBusy(null);
    if (message) setNote({ tone: 'info', text: message });
    else setOpen(false);
  };

  return (
    <>
      <Trigger
        ref={triggerRef}
        type="button"
        $open={open}
        $compact={compact}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={compact ? 'Download' : undefined}
        title={compact ? 'Download' : undefined}
        {...press}
      >
        <i className="fa-solid fa-download" aria-hidden="true" />
        {!compact && 'Download'}
        {!compact && (
          <Chevron $open={open} aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 4.5 6 7.5 9 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </Chevron>
        )}
      </Trigger>
      {pres.mounted && ReactDOM.createPortal(
        <Popover ref={popRef} $top={pos.top} $left={pos.left} $open={open && pres.entered} $up={pos.up} role="menu" aria-label={title}>
          <Heading data-dl-row>{title}</Heading>
          {choices.map((c) => (
            <Choice
              key={c.key}
              type="button"
              role="menuitem"
              data-dl-row
              $busy={busy === c.key}
              disabled={!!busy}
              aria-busy={busy === c.key || undefined}
              onClick={() => { void pick(c.key); }}
            >
              <IconTile $tone={c.tone} aria-hidden="true"><i className={c.icon} /></IconTile>
              <ChoiceText>
                <ChoiceLabel>{c.label}</ChoiceLabel>
                <ChoiceHint>{busy === c.key ? 'Preparing the file…' : c.hint}</ChoiceHint>
              </ChoiceText>
              {busy === c.key ? <Spinner aria-hidden="true" /> : <Tag>{c.tag}</Tag>}
            </Choice>
          ))}
          {note && <Note data-dl-row $tone={note.tone} role={note.tone === 'error' ? 'alert' : 'status'}>{note.text}</Note>}
        </Popover>,
        document.body,
      )}
    </>
  );
};

export default DownloadMenu;
